/**
 * MoneyBreakdownDialog.jsx — line items behind one Home money box.
 *
 * Opened by clicking Pipeline / Booked / Outstanding / Owed to Crew on the Home
 * page (admin only). Reads the already-computed result of lib/homeMoney.js, so
 * the modal can never disagree with the box that opened it.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fmtC } from '../../lib/utils';
import { navigateToJob } from '../../lib/crossNav';
import { useMobile } from '../../hooks/useMobile';

const monoSx = { fontFamily: '"DM Mono", monospace' };

const STATUS_LABEL = {
  intake: 'Intake', quoted: 'Quoted', confirmed: 'Confirmed', shooting: 'Shooting',
  post_production: 'Post production', partial_delivery: 'Partial delivery', delivered: 'Delivered',
  invoiced: 'Invoiced', paid: 'Paid', archived: 'Archived', void: 'Void',
};

function fmtDay(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function peopleText(people) {
  return people.map((p) => (people.length > 1 ? `${p.name} ${fmtC(p.amount)}` : p.name)).join(' · ');
}

function Row({ primary, secondary, amount, flags = [], onClick }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1, borderRadius: 1,
        ...(onClick ? {
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
        } : {}),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{primary}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {secondary && (
            <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.68rem' }}>{secondary}</Typography>
          )}
          {flags.map((f) => (
            <Chip key={f.label} label={f.label} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600, color: f.color, bgcolor: `${f.color}18` }} />
          ))}
        </Box>
      </Box>
      <Typography sx={{ ...monoSx, fontSize: '0.82rem', fontWeight: 600, flexShrink: 0 }}>{fmtC(amount)}</Typography>
    </Box>
  );
}

function Section({ title, total, children, muted = false }) {
  return (
    <Box sx={{ mt: 2, opacity: muted ? 0.75 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', px: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>{title}</Typography>
        {total != null && <Typography sx={{ ...monoSx, fontSize: '0.75rem', color: 'text.secondary' }}>{fmtC(total)}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function Empty({ children }) {
  return <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem', px: 1, py: 1 }}>{children}</Typography>;
}

const COPY = {
  pipeline: 'Leads and quotes the client has not confirmed yet (Intake or Quoted), at full value minus anything already paid. Money you hope for, not money you can count on.',
  booked: 'Confirmed work not invoiced yet: Confirmed through Delivered, plus any part of an invoiced job that has not been billed. Invoices already raised are subtracted. Their unpaid part is in Outstanding, and their paid part is already in the bank.',
  outstanding: 'Unpaid invoice balances on confirmed jobs: what clients owe you right now. Deposits already paid are subtracted. This matches Xero receivables, except deposit invoices on quotes that have not been accepted yet. Those stay in Pipeline.',
  crew: 'Crew pay (everyone except you) for finished work, from each job\'s pay split, minus what the Payroll grid shows as paid on that job. Production counts once a job reaches Post production, post once it is Delivered, and originator once it is Paid. Archived jobs count only where the Payroll grid still shows the line as unpaid. Mileage and admin time are not included.',
};

export default function MoneyBreakdownDialog({ kind, label, color, money, onClose }) {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [showNotCounted, setShowNotCounted] = useState(false);
  const data = money?.[kind];
  if (!kind || !data) return null;

  const goJob = (jobId) => () => { onClose(); navigateToJob(navigate, jobId); };
  const goInvoice = (id) => () => { onClose(); navigate(`/invoices?select=${id}`); };

  let body = null;

  if (kind === 'pipeline') {
    body = (
      <Section title={`${data.items.length} ${data.items.length === 1 ? 'job' : 'jobs'}`} total={data.total}>
        {data.items.length === 0 && <Empty>Nothing in the pipeline</Empty>}
        {data.items.map((it) => (
          <Row
            key={it.jobId}
            primary={it.title}
            secondary={[it.client, STATUS_LABEL[it.status], it.shootDate && `shoots ${fmtDay(it.shootDate)}`, it.less > 0 && `${fmtC(it.revenue)} − ${fmtC(it.less)} paid`].filter(Boolean).join(' · ')}
            flags={it.stale ? [{ label: 'shoot date passed', color: '#dc2626' }] : []}
            amount={it.amount}
            onClick={goJob(it.jobId)}
          />
        ))}
      </Section>
    );
  }

  if (kind === 'booked') {
    body = (
      <Section title={`${data.items.length} ${data.items.length === 1 ? 'job' : 'jobs'} to invoice`} total={data.total}>
        {data.items.length === 0 && <Empty>No confirmed work waiting to be invoiced</Empty>}
        {data.items.map((it) => (
          <Row
            key={it.jobId}
            primary={it.title}
            secondary={[it.client, STATUS_LABEL[it.status], it.less > 0 && `${fmtC(it.revenue)} − ${fmtC(it.less)} invoiced`].filter(Boolean).join(' · ')}
            amount={it.amount}
            onClick={goJob(it.jobId)}
          />
        ))}
      </Section>
    );
  }

  if (kind === 'outstanding') {
    body = (
      <>
        <Section title={`${data.items.length} unpaid ${data.items.length === 1 ? 'invoice' : 'invoices'}`} total={data.total}>
          {data.items.length === 0 && <Empty>No unpaid invoices</Empty>}
          {data.items.map((it) => (
            <Row
              key={it.invoiceId}
              primary={`${it.number} · ${it.client}`}
              secondary={[it.title, it.type && it.type !== 'full' && it.type, it.dueDate && `due ${fmtDay(it.dueDate)}`].filter(Boolean).join(' · ')}
              flags={it.daysOverdue > 0 ? [{ label: `${it.daysOverdue}d overdue`, color: '#dc2626' }] : []}
              amount={it.amount}
              onClick={goInvoice(it.invoiceId)}
            />
          ))}
        </Section>
        {data.excluded.length > 0 && (
          <Section title="Not counted: quote not accepted yet (in Pipeline)" total={data.excludedTotal} muted>
            {data.excluded.map((it) => (
              <Row
                key={it.invoiceId}
                primary={`${it.number} · ${it.client}`}
                secondary={[it.title, it.type && it.type !== 'full' && it.type, STATUS_LABEL[it.jobStatus]].filter(Boolean).join(' · ')}
                amount={it.amount}
                onClick={goInvoice(it.invoiceId)}
              />
            ))}
          </Section>
        )}
      </>
    );
  }

  if (kind === 'crew') {
    body = (
      <>
        <Section title="By person" total={data.total}>
          {data.people.length === 0 && <Empty>Nothing owed for finished work</Empty>}
          {data.people.map((p) => <Row key={p.name} primary={p.name} amount={p.amount} />)}
        </Section>
        {data.items.length > 0 && (
          <Section title="By job">
            {data.items.map((it) => (
              <Row
                key={it.jobId}
                primary={it.title}
                secondary={[peopleText(it.people), STATUS_LABEL[it.status],
                  it.archived ? 'unpaid in Payroll grid too'
                    : it.payroll === 0 ? 'not in Payroll grid yet'
                    : it.payroll != null ? `Payroll grid says ${fmtC(it.payroll)}` : null,
                ].filter(Boolean).join(' · ')}
                amount={it.amount}
                onClick={goJob(it.jobId)}
              />
            ))}
          </Section>
        )}
        {data.upcoming.length > 0 && (
          <Section title="Still to come: work not finished yet" total={data.upcomingTotal} muted>
            {data.upcoming.map((it) => (
              <Row
                key={it.jobId}
                primary={it.title}
                secondary={[peopleText(it.people), STATUS_LABEL[it.status]].filter(Boolean).join(' · ')}
                amount={it.amount}
                onClick={goJob(it.jobId)}
              />
            ))}
          </Section>
        )}
        {data.notCounted.length > 0 && (
          <Section title="Not counted: archived, ledger and Payroll disagree" total={data.notCountedTotal} muted>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pt: 0.75, fontSize: '0.68rem' }}>
              The pay split and payout ledger still show these as unpaid, but the Payroll grid has no unpaid line for them. Archived work is treated as settled.
            </Typography>
            {showNotCounted ? data.notCounted.map((it) => (
              <Row
                key={it.jobId}
                primary={it.title}
                secondary={peopleText(it.people)}
                amount={it.amount}
                onClick={goJob(it.jobId)}
              />
            )) : (
              <Button size="small" onClick={() => setShowNotCounted(true)} sx={{ ml: 0.5, mt: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>
                Show {data.notCounted.length} {data.notCounted.length === 1 ? 'job' : 'jobs'}
              </Button>
            )}
          </Section>
        )}
      </>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile} aria-labelledby="money-breakdown-title">
      <DialogTitle id="money-breakdown-title" sx={{ pr: 6, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.65rem', textTransform: 'uppercase', display: 'block' }}>
          {label}
        </Typography>
        <Typography component="span" sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', lineHeight: 1.1, color }}>
          {fmtC(data.total)}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>{COPY[kind]}</Typography>
        {body}
      </DialogContent>
    </Dialog>
  );
}
